/**
 * API para comunicação com o backend Python
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://discord-discord.nfkw2b.easypanel.host';

/**
 * Função para fazer requisições autenticadas
 */
export async function fetchWithAuth(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao realizar requisição');
  }

  return await response.json();
}

/**
 * Função para iniciar o login com Discord
 */
export async function loginWithDiscord() {
  try {
    const response = await fetch(`${API_URL}/auth/login`);
    const data = await response.json();
    window.location.href = data.url;
  } catch (error) {
    console.error('Erro ao iniciar login:', error);
    throw error;
  }
}

/**
 * Função para buscar o perfil do usuário
 */
export async function fetchUserProfile() {
  return await fetchWithAuth('/users/me');
}

/**
 * Função para buscar rankings
 */
export async function fetchRankings(type = 'global') {
  return await fetchWithAuth(`/rankings/${type}`);
}

/**
 * Função para buscar todos os rankings de uma vez
 */
export async function fetchAllRankings() {
  return await fetchWithAuth('/rankings/all');
}

/**
 * Funções para administração
 */
export async function fetchPendingUsers() {
  return await fetchWithAuth('/users/pending');
}

export async function fetchAllUsers() {
  return await fetchWithAuth('/users/all');
}

export async function approveUser(userId) {
  return await fetchWithAuth(`/users/approve/${userId}`, {
    method: 'PUT'
  });
}

export async function rejectUser(userId) {
  return await fetchWithAuth(`/users/reject/${userId}`, {
    method: 'PUT'
  });
}

export async function banUser(userId) {
  return await fetchWithAuth(`/users/ban/${userId}`, {
    method: 'PUT'
  });
}

export async function syncDiscordRole() {
  return await fetchWithAuth('/discord/sync', {
    method: 'POST'
  });
}

/**
 * Verificar cargo no servidor do Discord (verificação manual - não automática)
 */
export async function verifyDiscordRole(discordId) {
  return await fetchWithAuth(`/discord/verify/${discordId}`);
}

/**
 * Funções para Feed/Posts
 */

/**
 * Buscar posts do feed
 */
export async function fetchFeedPosts(limit = 20, offset = 0) {
  return await fetchWithAuth(`/feed/?limit=${limit}&offset=${offset}`);
}

/**
 * Buscar um post específico
 */
export async function fetchPost(postId) {
  return await fetchWithAuth(`/feed/${postId}`);
}

/**
 * Criar um novo post
 */
export async function createPost(postData) {
  return await fetchWithAuth('/feed/', {
    method: 'POST',
    body: JSON.stringify(postData)
  });
}

/**
 * Curtir um post
 */
export async function likePost(postId) {
  return await fetchWithAuth(`/feed/${postId}/like`, {
    method: 'POST'
  });
}

/**
 * Deletar um post
 */
export async function deletePost(postId) {
  return await fetchWithAuth(`/feed/${postId}`, {
    method: 'DELETE'
  });
}

/**
 * Buscar filmes na TMDb
 */
export async function searchMovies(query) {
  return await fetchWithAuth(`/feed/search/movie?query=${encodeURIComponent(query)}`);
}

/**
 * Buscar posts de um usuário específico
 */
export async function fetchUserPosts(userId) {
  return await fetchWithAuth(`/feed/user/${userId}`);
}

/**
 * Buscar comentários de um post
 */
export async function fetchPostComments(postId) {
  return await fetchWithAuth(`/feed/${postId}/comments`);
}

/**
 * Criar um comentário em um post
 */
export async function createComment(postId, content) {
  return await fetchWithAuth(`/feed/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content })
  });
}

/**
 * Deletar um comentário
 */
export async function deleteComment(commentId) {
  return await fetchWithAuth(`/feed/comments/${commentId}`, {
    method: 'DELETE'
  });
}

/**
 * Verificar se há um token válido
 */
export async function checkAuthStatus() {
  const token = localStorage.getItem('token');
  if (!token) {
    return { authenticated: false };
  }

  try {
    const response = await fetch(`${API_URL}/auth/check?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return await response.json();
  } catch (error) {
    console.error('Erro ao verificar autenticação:', error);
    return { authenticated: false };
  }
}

/**
 * Buscar perfil de um usuário específico
 */
export async function fetchUserProfileById(userId) {
  return await fetchWithAuth(`/users/profile/${userId}`);
}

/**
 * Buscar usuários por nome
 */
export async function searchUsers(query, limit = 10) {
  if (!query || query.length < 2) {
    return [];
  }
  return await fetchWithAuth(`/users/search?query=${encodeURIComponent(query)}&limit=${limit}`);
}

/**
 * Funções para Vídeos (Mural da Vergonha e Melhores Clipes)
 */

/**
 * Buscar vídeos por tipo (shame-wall ou best-clips)
 */
export async function fetchVideos(videoType, limit = 20, offset = 0) {
  return await fetchWithAuth(`/videos/${videoType}?limit=${limit}&offset=${offset}`);
}

/**
 * Buscar detalhes de um vídeo específico
 */
export async function fetchVideoDetail(videoType, videoId) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}`);
}

/**
 * Criar um novo vídeo
 */
export async function createVideo(videoType, videoData) {
  return await fetchWithAuth(`/videos/${videoType}`, {
    method: 'POST',
    body: JSON.stringify(videoData)
  });
}

/**
 * Atualizar um vídeo
 */
export async function updateVideo(videoType, videoId, videoData) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}`, {
    method: 'PUT',
    body: JSON.stringify(videoData)
  });
}

/**
 * Deletar um vídeo
 */
export async function deleteVideo(videoType, videoId) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}`, {
    method: 'DELETE'
  });
}

/**
 * Curtir/descurtir um vídeo
 */
export async function likeVideo(videoType, videoId) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}/like`, {
    method: 'POST'
  });
}

/**
 * Buscar comentários de um vídeo
 */
export async function fetchVideoComments(videoType, videoId) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}/comments`);
}

/**
 * Criar um comentário em um vídeo
 */
export async function createVideoComment(videoType, videoId, content) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content })
  });
}

/**
 * Deletar um comentário de vídeo
 */
export async function deleteVideoComment(videoType, videoId, commentId) {
  return await fetchWithAuth(`/videos/${videoType}/${videoId}/comments/${commentId}`, {
    method: 'DELETE'
  });
}

/**
 * Buscar vídeos de um usuário (autor ou mencionado)
 */
export async function fetchUserVideos(userId, videoType) {
  return await fetchWithAuth(`/videos/user/${userId}?video_type=${videoType}`);
}

/**
 * Funções para Rankings de Filmes
 */

/**
 * Buscar ranking global de filmes assistidos
 */
export async function fetchMovieRankingGlobal(limit = 10) {
  return await fetchWithAuth(`/movie-rankings/global?limit=${limit}`);
}

/**
 * Buscar ranking semanal de filmes assistidos
 */
export async function fetchMovieRankingWeekly(limit = 10) {
  return await fetchWithAuth(`/movie-rankings/weekly?limit=${limit}`);
}

/**
 * Buscar ranking mensal de filmes assistidos
 */
export async function fetchMovieRankingMonthly(limit = 10) {
  return await fetchWithAuth(`/movie-rankings/monthly?limit=${limit}`);
}

/**
 * Buscar todos os rankings de filmes de uma vez
 */
export async function fetchAllMovieRankings(limit = 10) {
  return await fetchWithAuth(`/movie-rankings/all?limit=${limit}`);
}

/**
 * Buscar posição de um usuário específico nos rankings de filmes
 */
export async function fetchUserMovieRanking(userId) {
  return await fetchWithAuth(`/movie-rankings/user/${userId}`);
}

/**
 * Funções para Stories
 */

/**
 * Buscar todas as stories ativas
 */
export async function fetchStories() {
  return await fetchWithAuth('/stories');
}

/**
 * Buscar uma story específica
 */
export async function fetchStory(storyId) {
  return await fetchWithAuth(`/stories/${storyId}`);
}

/**
 * Criar uma nova story (com FormData para upload de imagem)
 */
export async function createStory(formData) {
  // Para FormData, não usar fetchWithAuth pois precisamos evitar definir Content-Type
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Não autenticado');
  }

  const response = await fetch(`${API_URL}/stories`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Não definir Content-Type - o browser define automaticamente com boundary para FormData
    },
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
    throw new Error(error.detail || 'Erro ao criar story');
  }

  return await response.json();
}

/**
 * Registrar visualização de uma story
 */
export async function viewStory(storyId) {
  return await fetchWithAuth(`/stories/${storyId}/view`, {
    method: 'POST'
  });
}

/**
 * Deletar uma story
 */
export async function deleteStory(storyId) {
  return await fetchWithAuth(`/stories/${storyId}`, {
    method: 'DELETE'
  });
}